import type { ReactNode } from 'react';
import type { Formatter } from '@paige/format';
import type { AccountSummary } from './account-types';
import {
  PACE_SETTLES_DAYS,
  PACE_WARN,
  paceWord,
  percentWord,
  periodAge,
  planName,
} from './account-words';
import './insights.css';
import './account.css';

/** "40.5k" for the bullet's pace label, where room is short. */
function short(n: number): string {
  return n >= 10_000
    ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
    : Math.round(n).toLocaleString('en-US');
}

/**
 * The plan bullet (round 2, dataviz N2, sales M1): the track runs to the larger of the plan and the
 * pace; past the plan it is tinted, so going over is a zone and not a position. The solid fill is
 * what is used, the lighter fill runs on to the pace, and both marks are labelled in text: "plan
 * 10,000" under the tick, "pace 40.5k" over the dot.
 */
type Marks = { used: number; projected: number; plan: number };

/** Where a label sits against its mark: beside the other one when they are close, inward at the ends. */
function sideOf(share: (v: number) => number, v: number, other: number): string {
  if (Math.abs(share(v) - share(other)) < 0.22) return v <= other ? 'is-left' : 'is-right';
  if (share(v) > 0.8) return 'is-left';
  return share(v) < 0.15 ? 'is-right' : '';
}

function BulletLabels({ x, share }: { x: Marks; share: (v: number) => number }): ReactNode {
  const at = (v: number) => `${share(v) * 100}%`;
  if (Math.abs(share(x.projected) - share(x.plan)) < 0.22) {
    // Close marks share one label, set inward from the further one, so neither runs off the tile.
    const far = Math.max(x.plan, x.projected);
    return (
      <span className="ad-bullet2__labels" aria-hidden="true">
        <span
          className={share(far) > 0.5 ? 'is-left' : 'is-right'}
          style={{ insetInlineStart: at(far) }}
        >
          plan {short(x.plan)} · <b className="ad-bullet2__pace-label">pace {short(x.projected)}</b>
        </span>
      </span>
    );
  }
  return (
    <span className="ad-bullet2__labels" aria-hidden="true">
      <span className={sideOf(share, x.plan, x.projected)} style={{ insetInlineStart: at(x.plan) }}>
        plan {short(x.plan)}
      </span>
      <span
        className={`ad-bullet2__pace-label ${sideOf(share, x.projected, x.plan)}`}
        style={{ insetInlineStart: at(x.projected) }}
      >
        pace {short(x.projected)}
      </span>
    </span>
  );
}

export function PlanBullet(x: Marks): ReactNode {
  const top = Math.max(x.plan, x.projected) * 1.04 || 1;
  const share = (v: number) => Math.min(1, v / top);
  const at = (v: number) => `${share(v) * 100}%`;
  return (
    <span
      className="ad-bullet2"
      role="img"
      aria-label={`Used ${short(x.used)}, on pace for ${short(x.projected)}, plan ${short(x.plan)}`}
    >
      <span className="ad-bullet2__track">
        {x.projected > x.plan ? (
          <span className="ad-bullet2__over" style={{ insetInlineStart: at(x.plan) }} />
        ) : null}
        <span className="ad-bullet2__pace" style={{ inlineSize: at(x.projected) }} />
        <span className="ad-bullet2__used" style={{ inlineSize: at(x.used) }} />
        <span className="ad-bullet2__tick" style={{ insetInlineStart: at(x.plan) }} />
        <span className="ad-bullet2__dot" style={{ insetInlineStart: at(x.projected) }} />
      </span>
      <BulletLabels x={x} share={share} />
    </span>
  );
}

/** The plan tile leads with the pace; how much is used so far is its foot. */
export function PaceTile({
  a,
  today,
  f,
}: {
  a: AccountSummary;
  today: string;
  f: Formatter;
}): ReactNode {
  const c = a.credits;
  const age = periodAge(a, today);
  const tone = c.pace !== null && c.pace >= PACE_WARN ? ' ad-hero__tile--warning' : '';
  return (
    <a className={`ad-hero__tile${tone}`} href="#ad-ramp">
      <span className="ad-kpi__label">{planName(a.plan.key)} plan: pace this period</span>
      <span className="ad-hero__value">{paceWord(c.pace)}</span>
      {c.pace !== null ? (
        <PlanBullet
          used={c.usedThisPeriod}
          projected={c.projected}
          plan={a.plan.creditsPerPeriod}
        />
      ) : null}
      <span className="ad-kpi__foot">
        {percentWord(c.planUse)} used, {f.int(Math.round(c.usedThisPeriod))} of{' '}
        {f.int(a.plan.creditsPerPeriod)}, {age} day
        {age === 1 ? '' : 's'} in{age < PACE_SETTLES_DAYS ? ': early, the pace will move' : ''}
      </span>
    </a>
  );
}
